/**
 * Crew Utilization Report Page
 *
 * Shows crew utilization metrics including confirmation rates.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getCrewUtilization, downloadReportCSV, CrewUtilizationData } from '../../../api/reports';
import { getErrorMessage } from '../../../api/client';
import { ReportFilters, useReportDateRange } from '../../../components/reports';

export default function CrewUtilizationPage() {
  const [dateRange, setDateRange] = useReportDateRange();
  const [report, setReport] = useState<CrewUtilizationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getCrewUtilization({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
      });
      setReport(response.report);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadReportCSV(
        '/reports/crew-utilization',
        {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        },
        'crew-utilization.csv'
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <div>
      <nav className="mb-4">
        <Link to="/admin/reports" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Reports
        </Link>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Crew Utilization Report</h1>
        <p className="text-gray-600 mt-1">Assignment and confirmation rates by crew member</p>
      </div>

      <ReportFilters
        dateRange={dateRange}
        onChange={setDateRange}
        onExportCSV={handleExport}
        isExporting={isExporting}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading report...</p>
          </div>
        ) : report.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="text-sm font-medium text-gray-900">No data found</h3>
            <p className="mt-1 text-sm text-gray-500">No crew utilization data for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Crew Member</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Assigned</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Confirmed</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Declined</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Confirm Rate</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Pay</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {report.map((row) => (
                  <tr key={row.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/crew/${row.userId}`}
                        className="font-medium text-gray-900 hover:text-primary-600"
                      >
                        {row.firstName} {row.lastName}
                      </Link>
                      <p className="text-sm text-gray-500">{row.email}</p>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.assignedCount}</td>
                    <td className="px-6 py-4 text-right text-sm text-green-600">{row.confirmedCount}</td>
                    <td className="px-6 py-4 text-right text-sm text-red-600">{row.declinedCount}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        row.confirmationRate >= 80 ? 'bg-green-100 text-green-800' :
                        row.confirmationRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {row.confirmationRate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.totalHours.toFixed(1)}</td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(row.totalPay)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
